import React from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ErrorBoundary, Button, Input } from '@ohif/ui-next';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import { routerBasename } from '../utils/publicUrl';
import { useAppConfig } from '@state';
import { history } from '../utils/history';

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;

  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>
          One or more of the requested studies are not available at this time.
        </h4>
        {showStudyList && (
          <p className="mt-2">
            Return to the <Link className="text-primary-light" to="/">study list</Link> to select a different study to view.
          </p>
        )}
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

const RootWindow = () => {
  const [value, setValue] = React.useState('');
  // add form to add short url and when press OK, forward to the short url
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div className="bg-secondary-dark mx-auto space-y-2 rounded-lg py-8 px-8 drop-shadow-md">
        <div className="max-w-3xl">
          <h1 className="mb-7 block text-2xl">Willkommen!</h1>
          <span className="mb-2 block">
            Diese Web Applikation dient ausschließlich der Demonstration von Floy Produkten. Legen
            Sie gerne den Ordner einer CT Abdomen, CT Thorax oder CT Wirbelsäulen Studie in der
            Mitte des Fensters ab. Alternativ können Sie über den Knopf in der linken oberen
            Bildschirmecke eine Studie auswählen. Drücken Sie in der unteren Leiste auf den
            Play-Button neben „KI ausführen", sobald die Studie angezeigt wird. Je nach
            Internetverbindung und Dateigröße dauert die Anzeige des Ergebnis einige Minuten.
          </span>
          <span className="mb-2 block">
            Kontaktieren Sie zur Ergebnisbesprechung, bei Problemen oder für Fragen unseren
            Geschäftsführer Benedikt Schneider gerne direkt unter benedikt.schneider@floy.com oder
            +4915786031618.
          </span>
          <span className="mb-3 block">
            Bitte geben Sie hier Ihren Short URL ein, um Zugang zur Demo zu erhalten:
          </span>
          <div className="flex">
            <div className="mx-auto flex gap-[10px]">
              <Input
                className="max-w-xs rounded-lg text-xl"
                type="text"
                id="shortUrl"
                placeholder="Short URL"
                value={value}
                onChange={event => {
                  setValue(event.target.value);
                }}
              />
              <Button
                className="rounded-lg py-1.5 px-5 text-xl"
                onClick={() => {
                  if (!value) {
                    return;
                  }
                  window.location.href = '/short/' + value;
                }}
              >
                OK
              </Button>
            </div>
            <div className="flex flex-col">
              <Link
                className="text-primary-light"
                target="_blank"
                to={'https://www.floy.com/datenschutz'}
              >
                Datenschutz
              </Link>
              <Link
                className="text-primary-light"
                target="_blank"
                to={'https://www.floy.com/impressum'}
              >
                Impressum
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  {
    path: `/notfoundserver`,
    children: NotFoundServer,
  },
  {
    path: `/notfoundstudy`,
    children: NotFoundStudy,
  },
  /*  {
    path: '/debug',
    children: Debug,
  },
  {
    path: `/local`,
    children: Local.bind(null, { modePath: '' }), // navigate to the worklist
  },
  {
    path: `/localbasic`,
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  }, */
  {
    path: '/',
    children: RootWindow,
  },
];

// NOT FOUND (404)
const notFoundRoute = { path: '*', children: NotFound };

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const path =
    routerBasename.length > 1 && routerBasename.endsWith('/')
      ? routerBasename.substring(0, routerBasename.length - 1)
      : routerBasename;

  console.log('Registering worklist route', routerBasename, path);

  const WorkListRoute = {
    path: '/',
    children: DataSourceWrapper,
    private: true,
    props: { children: WorkList, servicesManager, extensionManager },
  };

  const customRoutes = customizationService.getCustomization('routes.customRoutes');

  const allRoutes = [
    ...routes,
    ...(showStudyList ? [WorkListRoute] : []),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    const [appConfig] = useAppConfig();
    const { showErrorDetails } = appConfig;

    history.navigate = useNavigate();

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <ErrorBoundary
        context={`Route ${route.path}`}
        showErrorDetails={showErrorDetails}
      >
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <Routes>
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <RouteWithErrorBoundary route={route} />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={<RouteWithErrorBoundary route={route} />}
          />
        );
      })}
    </Routes>
  );
};

export default createRoutes;
